/* ==========================================================================
   Barkly theme JS — replaces the React/framer-motion behaviour with
   dependency-free vanilla JS on top of Shopify's AJAX Cart API.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Money formatting (mirrors lib/format.ts) ---------- */

  function formatMoney(cents) {
    var format = (window.Barkly && window.Barkly.moneyFormat) || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var amount = parts.join('.');
    var amountNoDecimals = parts[0];
    return format
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, amountNoDecimals);
  }

  /* ---------- Scroll reveal (replaces FadeIn/Enter) ---------- */

  function initReveal(root) {
    var els = (root || document).querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (reduceMotion) {
      els.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    var obs = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '-64px' }
    );
    els.forEach(function (el) { obs.observe(el); });
  }

  /* ---------- Count-up stats (replaces <CountUp/>) ---------- */

  function initCountUp() {
    var els = document.querySelectorAll('[data-countup]');
    if (!els.length) return;

    function run(el) {
      var end = parseFloat(el.dataset.end || '0');
      var decimals = parseInt(el.dataset.decimals || '0', 10);
      var prefix = el.dataset.prefix || '';
      var suffix = el.dataset.suffix || '';
      if (reduceMotion) {
        el.textContent = prefix + end.toFixed(decimals) + suffix;
        return;
      }
      var duration = 1600;
      var start = null;
      function step(ts) {
        if (start === null) start = ts;
        var t = Math.min((ts - start) / duration, 1);
        var eased = 1 - Math.pow(1 - t, 3); /* approximates the site's ease-out */
        el.textContent = prefix + (end * eased).toFixed(decimals) + suffix;
        if (t < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }

    var obs = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            run(entry.target);
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '-40px' }
    );
    els.forEach(function (el) { obs.observe(el); });
  }

  /* ---------- Header scroll state ---------- */

  function initHeader() {
    var header = document.querySelector('[data-site-header]');
    if (!header) return;
    function onScroll() {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Mobile menu ---------- */

  function initMobileMenu() {
    var menu = document.querySelector('[data-mobile-menu]');
    if (!menu) return;
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-menu-open]')) {
        menu.hidden = false;
        document.body.classList.add('is-locked');
      }
      if (e.target.closest('[data-menu-close]') || (e.target.closest('a') && menu.contains(e.target))) {
        menu.hidden = true;
        document.body.classList.remove('is-locked');
      }
    });
  }

  /* ---------- Cart drawer (AJAX Cart API + Section Rendering API) ---------- */

  var cart = {
    open: function () {
      document.documentElement.classList.add('is-drawer-open');
      document.body.classList.add('is-locked');
    },
    close: function () {
      document.documentElement.classList.remove('is-drawer-open');
      document.body.classList.remove('is-locked');
    },
    /* Re-render the drawer + header bubble from the server so Liquid stays
       the single source of truth for cart markup. */
    refresh: function (openAfter) {
      return fetch(window.location.pathname + '?sections=cart-drawer,header')
        .then(function (r) { return r.json(); })
        .then(function (sections) {
          ['cart-drawer', 'header'].forEach(function (id) {
            var incoming = new DOMParser().parseFromString(sections[id], 'text/html');
            var target = document.getElementById('shopify-section-' + id);
            var source = incoming.getElementById('shopify-section-' + id) || incoming.body.firstElementChild;
            if (target && source) target.innerHTML = source.innerHTML;
          });
          if (openAfter) cart.open();
        });
    },
    add: function (variantId, quantity, openAfter) {
      return fetch(window.Barkly.routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity }),
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return cart.refresh(openAfter !== false);
      });
    },
    change: function (lineKey, quantity) {
      return fetch(window.Barkly.routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity }),
      }).then(function () { return cart.refresh(false); });
    },
  };
  window.BarklyCart = cart;

  function initCartDelegation() {
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-cart-open]')) { e.preventDefault(); cart.open(); }
      if (e.target.closest('[data-cart-close]')) { e.preventDefault(); cart.close(); }

      var changeBtn = e.target.closest('[data-cart-change]');
      if (changeBtn) {
        e.preventDefault();
        changeBtn.disabled = true;
        cart.change(changeBtn.dataset.line, parseInt(changeBtn.dataset.quantity, 10));
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') cart.close();
    });
  }

  /* ---------- Product form (variant swatches, qty, ATC, buy now) ---------- */

  function initProductForm() {
    var form = document.querySelector('[data-product-form]');
    if (!form) return;

    var dataEl = document.getElementById('barkly-product-data');
    var variants = dataEl ? JSON.parse(dataEl.textContent) : [];

    var idInput = form.querySelector('[name="id"]');
    var qtyEl = form.querySelector('[data-qty-value]');
    var image = document.querySelector('[data-main-image]');
    var stockEl = document.querySelector('[data-stock-note]');
    var colorNameEl = document.querySelector('[data-color-name]');
    var atcBtn = form.querySelector('[data-add-to-cart]');
    var atcPriceEls = document.querySelectorAll('[data-atc-price]');

    function currentVariant() {
      var id = parseInt(idInput.value, 10);
      return variants.find(function (v) { return v.id === id; }) || variants[0];
    }
    function qty() { return parseInt(qtyEl.textContent, 10) || 1; }

    function render() {
      var v = currentVariant();
      if (!v) return;
      var available = v.available;

      form.querySelectorAll('[data-variant-swatch]').forEach(function (btn) {
        btn.classList.toggle('is-active', parseInt(btn.dataset.variantId, 10) === v.id);
      });
      if (colorNameEl) colorNameEl.textContent = v.title;
      if (stockEl) {
        stockEl.textContent = available
          ? (v.inventory_quantity > 0 ? v.inventory_quantity + ' in stock' : 'In stock')
          : 'Out of stock';
        stockEl.classList.toggle('is-out', !available);
      }
      if (image && v.featured_image) {
        image.src = v.featured_image.src;
        image.srcset = '';
        image.alt = v.featured_image.alt || v.title;
      }
      atcPriceEls.forEach(function (el) { el.textContent = formatMoney(v.price * qty()); });
      form.querySelectorAll('[data-add-to-cart], [data-buy-now]').forEach(function (b) {
        b.disabled = !available;
      });
      if (atcBtn && !available) atcBtn.querySelector('[data-atc-label]').textContent = 'Out of stock';
    }

    form.addEventListener('click', function (e) {
      var swatch = e.target.closest('[data-variant-swatch]');
      if (swatch) {
        idInput.value = swatch.dataset.variantId;
        render();
      }
      var step = e.target.closest('[data-qty-step]');
      if (step) {
        var v = currentVariant();
        var max = v && v.inventory_quantity > 0 ? v.inventory_quantity : 99;
        qtyEl.textContent = Math.min(max, Math.max(1, qty() + parseInt(step.dataset.qtyStep, 10)));
        render();
      }
    });

    function submit(buyNow) {
      var v = currentVariant();
      if (!v) return;
      form.querySelectorAll('button').forEach(function (b) { b.disabled = true; });
      cart
        .add(v.id, qty(), !buyNow)
        .then(function () {
          if (buyNow) {
            window.location.href = '/checkout';
          } else {
            var label = form.querySelector('[data-atc-label]');
            if (label) {
              var original = label.textContent;
              label.textContent = 'Added to cart ✓';
              setTimeout(function () { label.textContent = original; render(); }, 1800);
            }
          }
        })
        .catch(function (err) {
          alert((err && err.description) || 'Could not add to cart.');
        })
        .finally(function () {
          form.querySelectorAll('button').forEach(function (b) { b.disabled = false; });
          render();
        });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      submit(false);
    });
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-buy-now]')) { e.preventDefault(); submit(true); }
      if (e.target.closest('[data-sticky-atc]')) { e.preventDefault(); submit(false); }
      if (e.target.closest('[data-sticky-buy]')) { e.preventDefault(); submit(true); }
    });

    /* Sticky add-to-cart bar appears once the main CTA scrolls above the fold */
    var sentinel = document.querySelector('[data-cta-sentinel]');
    var sticky = document.querySelector('[data-sticky-bar]');
    if (sentinel && sticky) {
      new IntersectionObserver(function (entries) {
        var entry = entries[0];
        sticky.classList.toggle(
          'is-visible',
          !entry.isIntersecting && entry.boundingClientRect.top < 0
        );
      }).observe(sentinel);
    }

    render();
  }

  /* ---------- Boot ---------- */

  document.addEventListener('DOMContentLoaded', function () {
    initReveal();
    initCountUp();
    initHeader();
    initMobileMenu();
    initCartDelegation();
    initProductForm();
  });

  /* Re-run reveal for content swapped in by the theme editor */
  document.addEventListener('shopify:section:load', function (e) {
    initReveal(e.target);
  });
})();
